import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createQueryKey,
  PARAGON_SECRETS_QUERY_KEY,
} from 'constants/QueryKeys.constants';

import type {
  IntegrationEnvironment,
  IntegrationProvider,
} from '@polyai/schemas';
import {
  connectIntegration,
  disableIntegration,
  disconnectIntegration,
  enableIntegration,
} from '@polyai/schemas';

import {
  deleteParagonSecret as apiDeleteParagonSecret,
  getParagonSecret as apiGetParagonSecret,
  upsertParagonSecret as apiUpsertParagonSecret,
} from 'api/resources/paragon';
import type { ParagonSecretStructure } from 'api/resources/paragon/types';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useIsFeatureOn } from './useFeatureSwitch/useIsFeatureOn';
import useProjectRoute from './useProjectRoute';
import { useToast } from './useToast';

type ParagonEnvironment = 'sandbox' | 'preRelease' | 'live';

const EMPTY_PARAGON_SECRET: ParagonSecretStructure = {
  connections: {},
  sandbox: {},
  preRelease: {},
  live: {},
};

const deepCopyEnv = (env: Record<string, string[]>): Record<string, string[]> =>
  Object.fromEntries(Object.entries(env).map(([k, v]) => [k, [...v]]));

/**
 * Hook for managing Paragon credentials via secrets API
 */
export function useParagonSecrets() {
  const { accountId, projectId } = useProjectRoute();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isFeatureOn } = useIsFeatureOn();
  const isEnabled = isFeatureOn('paragon_secrets');

  // Memoize queryKey to prevent re-subscriptions
  const queryKey = useMemo(() => {
    const key =
      accountId && projectId
        ? createQueryKey(accountId, projectId, PARAGON_SECRETS_QUERY_KEY)
        : [];
    return key;
  }, [accountId, projectId]);

  // Get Paragon secrets
  const { data: secret, isLoading } = useQuery<ParagonSecretStructure>(
    queryKey,
    async () => {
      try {
        return await apiGetParagonSecret(accountId!, projectId!);
      } catch (error: any) {
        // Return empty structure if not found (404 or JSON parse error from HTML 404 page)
        // Check for HTTPError with 404 status OR SyntaxError from parsing HTML as JSON
        if (
          error?.httpStatus === 404 ||
          (error instanceof SyntaxError &&
            error.message.includes('Unexpected token'))
        ) {
          return EMPTY_PARAGON_SECRET;
        }
        throw error;
      }
    },
    {
      enabled: Boolean(accountId && projectId && isEnabled),
      retry: false,
      refetchOnMount: false, // Don't refetch when component mounts
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  );

  // Upsert Paragon secrets
  const upsertMutation = useMutation({
    mutationFn: async (newSecret: ParagonSecretStructure) => {
      return await apiUpsertParagonSecret(accountId!, projectId!, newSecret);
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error({
        title: 'Failed to update credentials',
        description: error.message,
      });
    },
  });

  // Delete Paragon secrets
  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await apiDeleteParagonSecret(accountId!, projectId!);
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error({
        title: 'Failed to delete credentials',
        description: error.message,
      });
    },
  });

  return {
    secret: secret ?? EMPTY_PARAGON_SECRET,
    isLoading,
    upsertSecret: upsertMutation.mutateAsync,
    deleteSecret: deleteMutation.mutate,
    isUpserting: upsertMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
}

/**
 * Helper hook for managing individual connections (Secrets API implementation)
 * This is the internal implementation - use useParagonIntegrationActions for the unified interface
 */
function useParagonConnectionInternal() {
  const { secret, upsertSecret, isUpserting } = useParagonSecrets();

  const cloneSecret = (): ParagonSecretStructure => ({
    connections: { ...secret.connections },
    sandbox: deepCopyEnv(secret.sandbox),
    preRelease: deepCopyEnv(secret.preRelease),
    live: deepCopyEnv(secret.live),
  });

  const addConnection = (
    connectionId: string,
    connectionData: ParagonSecretStructure['connections'][string],
    environment: ParagonEnvironment,
  ) => {
    const newSecret = cloneSecret();

    // Add connection
    newSecret.connections[connectionId] = connectionData;

    // Add to environment
    const provider = connectionData.provider;
    if (!newSecret[environment][provider]) {
      newSecret[environment][provider] = [];
    }
    if (!newSecret[environment][provider].includes(connectionId)) {
      newSecret[environment][provider].push(connectionId);
    }

    upsertSecret(newSecret);
  };

  const removeConnection = (connectionId: string) => {
    const newSecret = cloneSecret();

    // Remove from all environments
    for (const env of [
      'sandbox',
      'preRelease',
      'live',
    ] satisfies ParagonEnvironment[]) {
      for (const provider in newSecret[env]) {
        newSecret[env][provider] = newSecret[env][provider].filter(
          id => id !== connectionId,
        );
        // Clean up empty arrays
        if (newSecret[env][provider].length === 0) {
          delete newSecret[env][provider];
        }
      }
    }

    // Remove connection
    delete newSecret.connections[connectionId];

    upsertSecret(newSecret);
  };

  const enableInEnvironment = async (
    connectionId: string,
    environment: ParagonEnvironment,
  ) => {
    const connection = secret.connections[connectionId];
    if (!connection) {
      return;
    }

    const newSecret = cloneSecret();
    const provider = connection.provider;

    if (!newSecret[environment][provider]) {
      newSecret[environment][provider] = [];
    }
    if (!newSecret[environment][provider].includes(connectionId)) {
      newSecret[environment][provider].push(connectionId);
    }

    await upsertSecret(newSecret);
  };

  const disableInEnvironment = async (
    connectionId: string,
    environment: ParagonEnvironment,
  ) => {
    const connection = secret.connections[connectionId];
    if (!connection) {
      return;
    }

    const newSecret = cloneSecret();
    const provider = connection.provider;

    if (newSecret[environment][provider]) {
      newSecret[environment][provider] = newSecret[environment][
        provider
      ].filter(id => id !== connectionId);
      // Clean up empty arrays
      if (newSecret[environment][provider].length === 0) {
        delete newSecret[environment][provider];
      }
    }

    await upsertSecret(newSecret);
  };

  const updateConnectionConfig = (
    connectionId: string,
    updates: Partial<ParagonSecretStructure['connections'][string]>,
  ) => {
    const connection = secret.connections[connectionId];
    if (!connection) {
      return;
    }

    const newSecret = cloneSecret();

    // Update the connection with new properties
    newSecret.connections[connectionId] = {
      ...connection,
      ...updates,
    };

    upsertSecret(newSecret);
  };

  return {
    addConnection,
    removeConnection,
    enableInEnvironment,
    disableInEnvironment,
    updateConnectionConfig,
    isLoading: isUpserting,
  };
}

/**
 * Unified hook for managing Paragon integration connections
 * Automatically routes to Secrets API or Redux based on feature flag
 * Use this hook instead of directly accessing secrets API or Redux actions
 */
export function useParagonIntegrationActions(provider: IntegrationProvider) {
  const { isFeatureOn } = useIsFeatureOn();
  const useSecretsAPI = isFeatureOn('paragon_secrets');
  const dispatch = useServerDispatch();
  const { accountId, projectId } = useProjectRoute();

  // Secrets API methods
  const secretsActions = useParagonConnectionInternal();

  // Unified interface that routes based on feature flag
  const connectConnection = async (
    connectionData: ParagonSecretStructure['connections'][string],
    environment: IntegrationEnvironment,
    additionalEnvironments: IntegrationEnvironment[] = [],
  ) => {
    const paragonConnectionId = connectionData.paragonConnectionId;
    const paragonUserId = `${accountId}_${projectId}`;

    if (useSecretsAPI) {
      // Use Secrets API
      secretsActions.addConnection(
        paragonConnectionId,
        connectionData,
        environment,
      );

      // Enable in additional environments
      for (const env of additionalEnvironments) {
        await secretsActions.enableInEnvironment(paragonConnectionId, env);
      }
    } else {
      // Use Redux
      dispatch(
        connectIntegration({
          provider,
          clientEnv: environment,
          paragonConnectionId,
          paragonUserId,
          metadata: connectionData.metadata as any,
        }),
      );

      for (const env of additionalEnvironments) {
        dispatch(
          connectIntegration({
            provider,
            clientEnv: env,
            paragonConnectionId,
            paragonUserId,
            metadata: connectionData.metadata as any,
          }),
        );
      }
    }
  };

  const disconnectConnection = async (connectionId: string) => {
    if (useSecretsAPI) {
      secretsActions.removeConnection(connectionId);
    } else {
      dispatch(
        disconnectIntegration({
          provider,
          paragonConnectionId: connectionId,
        }),
      );
    }
  };

  const enableConnectionInEnvironment = async (
    connectionId: string,
    environment: IntegrationEnvironment,
  ) => {
    if (useSecretsAPI) {
      await secretsActions.enableInEnvironment(connectionId, environment);
    } else {
      dispatch(
        enableIntegration({
          provider,
          paragonConnectionId: connectionId,
          clientEnv: environment,
        }),
      );
    }
  };

  const disableConnectionInEnvironment = async (
    connectionId: string,
    environment: IntegrationEnvironment,
  ) => {
    if (useSecretsAPI) {
      await secretsActions.disableInEnvironment(connectionId, environment);
    } else {
      dispatch(
        disableIntegration({
          provider,
          paragonConnectionId: connectionId,
          clientEnv: environment,
        }),
      );
    }
  };

  return {
    connectConnection,
    disconnectConnection,
    enableInEnvironment: enableConnectionInEnvironment,
    disableInEnvironment: disableConnectionInEnvironment,
    isLoading: useSecretsAPI ? secretsActions.isLoading : false,
  };
}

// Legacy export for backward compatibility
export const useParagonConnection = useParagonConnectionInternal;
